import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Loader2, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useTrialResultCodes, type TrialResultCode } from '@/api/trialResultCodes';
import type { EntryWidgetProps } from './types';

/*
 * Trial grid — a fixed row of N cells (from target.trials). Clicking a cell
 * opens a Radix DropdownMenu of practice-configured result codes (I, V, G,
 * PP, FP, NR by default; admin-editable under Configuration → Clinical →
 * Result Codes). The selected code is stamped into the cell.
 *
 * valueJson.trials on state holds a (string | null)[] of length trialsCount
 * — null for cells the clinician has not scored yet. On save, the pane's
 * `handleSave` filters nulls out so the server sees only actual trials.
 * Server recomputes value_numeric from each code's is_correct flag; the
 * in-widget % is just a display hint.
 */
export function TrialGrid({
    value,
    onChange,
    disabled,
    trialsCount,
}: EntryWidgetProps & { trialsCount: number }) {
    const codesQuery = useTrialResultCodes();
    const codes = codesQuery.data ?? [];
    const activeCodes = codes.filter((c) => !c.archivedAt);
    const codeByValue = new Map(codes.map((c) => [c.code, c]));

    const stored: (string | null)[] = (() => {
        const fromState = value.valueJson && 'trials' in value.valueJson
            ? (value.valueJson.trials as unknown[])
            : null;
        const init: (string | null)[] = Array.from({ length: trialsCount }, () => null);
        if (fromState) {
            for (let i = 0; i < Math.min(fromState.length, trialsCount); i++) {
                const v = fromState[i];
                init[i] = typeof v === 'string' && v ? v : null;
            }
        }
        return init;
    })();

    const setCell = (i: number, code: string | null) => {
        if (disabled) return;
        const next = stored.slice();
        next[i] = code;
        const scored = next.filter((c): c is string => c !== null);
        const correct = scored.filter((c) => codeByValue.get(c)?.isCorrect).length;
        const pct = scored.length > 0 ? Math.round((correct / scored.length) * 100) : null;
        onChange({
            ...value,
            valueNumeric: pct,
            // Hand the full array (nulls included) through local state so
            // the grid stays the right length; the pane will strip nulls
            // before POST/PATCH.
            valueJson: { trials: next as string[] },
            dirty: true,
        });
    };

    const scored = stored.filter((c): c is string => c !== null);
    const correctCount = scored.filter((c) => codeByValue.get(c)?.isCorrect).length;

    if (codesQuery.isLoading) {
        return <div className="flex items-center gap-2 text-xs text-text-subtle"><Loader2 className="w-3 h-3 animate-spin" /> Loading result codes…</div>;
    }
    if (codesQuery.isError) {
        return <div className="text-xs text-error">Couldn't load result codes.</div>;
    }
    if (activeCodes.length === 0) {
        return (
            <div className="text-xs text-text-subtle">
                No result codes configured. Add codes under Configuration → Clinical → Result Codes.
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <div className="flex flex-wrap gap-1.5">
                {stored.map((cell, i) => (
                    <TrialCellButton
                        key={i}
                        index={i}
                        code={cell}
                        codeRow={cell ? codeByValue.get(cell) ?? null : null}
                        disabled={disabled}
                        activeCodes={activeCodes}
                        onPick={(c) => setCell(i, c)}
                        onClear={() => setCell(i, null)}
                    />
                ))}
            </div>
            <div className="text-xs text-text-subtle">
                {scored.length}/{trialsCount} scored · {correctCount} correct ·{' '}
                <span className="font-semibold text-text">
                    {value.valueNumeric === null ? '—' : `${value.valueNumeric}%`}
                </span>
            </div>
        </div>
    );
}

function TrialCellButton({
    index, code, codeRow, disabled, activeCodes, onPick, onClear,
}: {
    index: number;
    code: string | null;
    codeRow: TrialResultCode | null;
    disabled?: boolean;
    activeCodes: TrialResultCode[];
    onPick: (code: string) => void;
    onClear: () => void;
}) {
    const label = code ?? String(index + 1);
    const hasColor = !!codeRow?.color;
    const isCorrect = codeRow?.isCorrect === true;
    const isIncorrect = codeRow && codeRow.isCorrect === false;
    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild disabled={disabled}>
                <button
                    type="button"
                    aria-label={`Trial ${index + 1}: ${codeRow ? `${codeRow.code} — ${codeRow.label}` : 'not scored'}`}
                    title={codeRow ? `${codeRow.code} — ${codeRow.label}` : `Trial ${index + 1}`}
                    disabled={disabled}
                    className={cn(
                        'min-w-9 h-9 px-1.5 rounded-md border text-sm font-bold flex items-center justify-center',
                        'transition-colors duration-[var(--duration-base)]',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                        !code && 'bg-background border-border text-text-subtle hover:bg-surface-muted',
                        code && !hasColor && isCorrect   && 'bg-accent border-accent-dark text-text',
                        code && !hasColor && isIncorrect && 'bg-warning-soft border-warning text-warning',
                    )}
                    style={hasColor
                        ? { backgroundColor: codeRow!.color!, color: '#fff', borderColor: codeRow!.color! }
                        : undefined}
                >
                    {label}
                </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    align="start"
                    sideOffset={4}
                    className="min-w-[180px] bg-surface border border-border rounded-md shadow-md py-1 z-[60]"
                >
                    {activeCodes.map((c) => {
                        const hasItemColor = !!c.color;
                        return (
                            <DropdownMenu.Item
                                key={c.id}
                                onSelect={() => onPick(c.code)}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm text-text hover:bg-surface-muted cursor-pointer outline-none"
                            >
                                <span
                                    className={cn(
                                        'min-w-6 px-1.5 text-center rounded text-xs font-bold',
                                        !hasItemColor && c.isCorrect  && 'bg-accent text-text',
                                        !hasItemColor && !c.isCorrect && 'bg-warning-soft text-warning',
                                    )}
                                    style={hasItemColor ? { backgroundColor: c.color!, color: '#fff' } : undefined}
                                >
                                    {c.code}
                                </span>
                                <span className="flex-1">{c.label}</span>
                            </DropdownMenu.Item>
                        );
                    })}
                    {code && (
                        <>
                            <DropdownMenu.Separator className="my-1 h-px bg-border" />
                            <DropdownMenu.Item
                                onSelect={() => onClear()}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm text-text-muted hover:bg-surface-muted cursor-pointer outline-none"
                            >
                                <X className="w-3 h-3" /> Clear
                            </DropdownMenu.Item>
                        </>
                    )}
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}
